import { useState } from "react";
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Typography,
} from "@mui/material";
import { ChevronDown, ChevronUp } from "lucide-react";
import Select from "components/common/Select";
import { Advisor } from "protogen/common_pb";
import { LIGHTNING_TASK_TYPES } from "types/delegation";

export type AdvisorServiceDefinition = {
  value: string;
  title: string;
  description: string | null;
};

export const ADVISOR_SERVICES: Record<string, AdvisorServiceDefinition> =
  Object.fromEntries(
    Object.values(LIGHTNING_TASK_TYPES).map((service) => [
      service.value.toUpperCase().replace(/-/g, "_"),
      {
        value: service.value,
        title: service.title,
        description: service.description,
      },
    ]),
  );

export type ServiceScope = "browse" | "notify" | "disabled";

type AdvisorServiceProps = {
  service: AdvisorServiceDefinition;
  advisor: Advisor;
  loading: boolean;
  onServiceChange: (
    serviceName: string,
    enabled: boolean,
    scope: ServiceScope,
  ) => Promise<void>;
};

export const AdvisorServiceRow = ({
  service,
  advisor,
  loading,
  onServiceChange,
}: AdvisorServiceProps) => {
  const isEnabled = advisor.enabledServices.includes(service.value);
  const currentScope =
    (advisor.serviceScopes[service.value] as ServiceScope) || "disabled";

  const currentValue = isEnabled ? currentScope : "disabled";

  const onChange = async (newValue: string) => {
    const enabled = newValue !== "disabled";
    const scope = newValue as ServiceScope;
    await onServiceChange(service.value, enabled, scope);
  };

  return (
    <ListItem sx={{ padding: "4px 0", gap: "16px" }}>
      <ListItemText
        primary={service.title}
        secondary={service.description}
        primaryTypographyProps={{ variant: "bodySmall" }}
        secondaryTypographyProps={{ variant: "bodySmall" }}
      />
      <Select
        size="small"
        variant="standard"
        value={currentValue}
        onChange={(e) => onChange(e.target.value as string)}
        disabled={loading}
        sx={{ minWidth: 140 }}
      >
        <MenuItem value="disabled">Disabled</MenuItem>
        <MenuItem value="browse">Enabled - Browse</MenuItem>
        <MenuItem value="notify">Enabled - Notify</MenuItem>
      </Select>
    </ListItem>
  );
};

type AdvisorServicesSectionProps = {
  advisor: Advisor;
  loading: boolean;
  onServiceChange: (
    serviceName: string,
    enabled: boolean,
    scope: ServiceScope,
  ) => Promise<void>;
  collapsible?: boolean;
  title?: string;
};

export const AdvisorServicesSection = ({
  advisor,
  loading,
  onServiceChange,
  collapsible = true,
  title = "Advisor Services",
}: AdvisorServicesSectionProps) => {
  const [expanded, setExpanded] = useState(!collapsible);
  const services = Object.values(ADVISOR_SERVICES);
  if (services.length === 0) return null;

  return (
    <Box sx={{ marginTop: collapsible ? "12px" : 0 }}>
      {collapsible ? (
        <Box
          display="flex"
          alignItems="center"
          sx={{ cursor: "pointer" }}
          onClick={() => setExpanded(!expanded)}
        >
          <Typography variant="bodyHeavy" sx={{ marginRight: "4px" }}>
            {title}
          </Typography>
          <IconButton size="small">
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </IconButton>
        </Box>
      ) : (
        <Typography variant="h4" sx={{ marginBottom: "8px" }}>
          {title}
        </Typography>
      )}
      <Collapse in={expanded} timeout="auto" unmountOnExit={collapsible}>
        <List dense sx={{ padding: 0 }}>
          {services.map((service) => (
            <AdvisorServiceRow
              key={service.value}
              service={service}
              advisor={advisor}
              loading={loading}
              onServiceChange={onServiceChange}
            />
          ))}
        </List>
      </Collapse>
    </Box>
  );
};
